import {
  FormGroup,
  FormGroupProps,
} from "@jugl-web/ui-components/cross-platform/forms";
import { SelectTrigger } from "@jugl-web/ui-components/cross-platform/forms/SelectTrigger";
import { ResourcePickerDrawer } from "@jugl-web/ui-components/mobile/ResourcePickerDrawer";
import { ResourcePickerPopover } from "@jugl-web/ui-components/web/ResourcePickerPopover";
import { cx, useAppVariant, useTranslations } from "@jugl-web/utils";
import { FC, useMemo, useState } from "react";
import type { Placement } from "@floating-ui/react";
import { AppModule } from "../../../common/types";
import { useUserListBox } from "../../hooks/useUserListBox";
import { UsersAvatarStack } from "../UsersAvatarStack";
import { ReactComponent as PlusIcon } from "./assets/plus.svg";
import { ReactComponent as ChevronIcon } from "./assets/chevron.svg";

interface UserSelectProps extends FormGroupProps {
  entityId: string;
  meId: string;
  userIds: string[];
  userTypeLabel?: "user" | "member";
  icon?: "plus" | "chevron";
  placeholder?: string;
  isReadonly?: boolean;
  module?: AppModule;
  dialogTitle?: string;
  inputLabel?: string;
  emptyStateLabel?: string;
  placement?: Placement;
  onUsersChange?: (userIds: string[]) => void;
  shouldExcludeMe?: boolean;
}

export const UserSelect: FC<UserSelectProps> = ({
  entityId,
  meId,
  userIds,
  userTypeLabel = "user",
  icon = "plus",
  placeholder,
  isReadonly = false,
  module,
  dialogTitle,
  inputLabel,
  emptyStateLabel,
  placement = "right",
  onUsersChange,
  shouldExcludeMe,
  ...formGroupProps
}) => {
  const [isMobileDialogOpen, setIsMobileDialogOpen] = useState(false);

  const { isMobile } = useAppVariant();

  const [userIdsWithoutMe, wasMeUserFilteredOut] = useMemo(() => {
    const filteredUserIds = userIds.filter((userId) => userId !== meId);
    return [filteredUserIds, filteredUserIds.length < userIds.length];
  }, [meId, userIds]);

  const { getListBoxProps, clearSearchQuery } = useUserListBox({
    entityId,
    excludeUsers: !isReadonly && shouldExcludeMe ? [meId] : undefined,
    topUsers: shouldExcludeMe ? userIdsWithoutMe : userIds,
    module,
    showDepartmentFilters: true,
  });

  const handleUsersChange = (ids: string[]) => {
    if (!onUsersChange) {
      return;
    }

    if (wasMeUserFilteredOut) {
      onUsersChange([meId, ...ids]);
    } else {
      onUsersChange(ids);
    }
  };

  const { t } = useTranslations();

  const userTypeLabelObj =
    userTypeLabel === "user"
      ? {
          id: "user-select-component.users-count",
          defaultMessage: "{count} {count, plural, one {user} other {users}}",
        }
      : {
          id: "user-select-component.members-count",
          defaultMessage:
            "{count} {count, plural, one {member} other {members}}",
        };

  const triggerLabel = (() => {
    if (userIds.length > 0) {
      return inputLabel || t(userTypeLabelObj, { count: userIds.length });
    }

    return (
      emptyStateLabel ||
      (isReadonly
        ? t({
            id: "user-select-component.no-users",
            defaultMessage: "No users",
          })
        : placeholder ||
          t({ id: "common.tap-to-select", defaultMessage: "Tap to select" }))
    );
  })();

  const triggerIcon = (isOpen = false) =>
    (() => {
      if (userIds.length > 0) {
        return (
          <UsersAvatarStack
            entityId={entityId}
            userIds={userIds}
            maxCount={5}
            size="lg"
          />
        );
      }

      if (isReadonly) {
        return null;
      }

      if (icon === "chevron")
        return (
          <span
            className={cx(
              "transform transition-transform",
              isOpen && "rotate-180"
            )}
          >
            <ChevronIcon />
          </span>
        );

      return <PlusIcon />;
    })();

  const commonResourcePickerProps = {
    onSubmit: (ids: string[]) => {
      handleUsersChange(ids);
      clearSearchQuery();
    },
    ...getListBoxProps({
      selectionBehavior: { mode: isReadonly ? "readonly" : "multiple" },
      defaultSelectedIds: isReadonly ? userIds : userIdsWithoutMe,
    }),
  };

  if (isMobile) {
    return (
      <>
        <FormGroup {...formGroupProps}>
          <SelectTrigger
            label={triggerLabel}
            isOpen={isMobileDialogOpen}
            customIcon={triggerIcon(isMobileDialogOpen)}
            className="h-[58px] w-full"
            onClick={() => setIsMobileDialogOpen(true)}
          />
        </FormGroup>
        <ResourcePickerDrawer
          isOpen={isMobileDialogOpen}
          title={
            dialogTitle ??
            t({
              id: "user-select-component.select-users",
              defaultMessage: "Select users",
            })
          }
          onClose={() => setIsMobileDialogOpen(false)}
          {...commonResourcePickerProps}
        />
      </>
    );
  }

  return (
    <FormGroup {...formGroupProps}>
      <ResourcePickerPopover
        placement={placement}
        className="w-[375px]"
        renderTrigger={({ Trigger, triggerRef, isOpen: isPopoverOpen }) => (
          <Trigger
            ref={triggerRef}
            as={SelectTrigger}
            label={triggerLabel}
            isOpen={isPopoverOpen}
            customIcon={triggerIcon(isPopoverOpen)}
            className="h-[58px] w-full"
          />
        )}
        onUnmount={clearSearchQuery}
        {...commonResourcePickerProps}
      />
    </FormGroup>
  );
};
